/**
 * People — the shared rules for listing staff and showing their names (Phase 2 of the
 * task-notification plan, owner ruling HDQ-TASKN-5).
 *
 * Two facts this module exists to make impossible to get wrong:
 *  1. A person is identified by their LOGIN id (profiles.user_id = auth.uid()), never by
 *     the profile primary key (profiles.id). On prod no profile has id = user_id, so the
 *     wrong one matches nothing — the bug class behind several past defects.
 *  2. Copied display names (task_assignments.assigned_to_name and friends) go stale when
 *     someone is renamed. Screens resolve the CURRENT name from the login id and fall
 *     back to the copied name only when the person can't be found (a removed login, or
 *     the people list failed to load).
 */

export interface ProfileRow {
  user_id: string | null;
  full_name: string | null;
  email: string | null;
  is_test_account?: boolean | null;
  avatar_url?: string | null;
}

export interface Person {
  userId: string;
  name: string;
  email: string | null;
  isTestAccount: boolean;
  avatarUrl: string | null;
}

/**
 * Reads every page of a ranged query until a SHORT page proves there is no more.
 * `fetchPage(from, to)` is inclusive, matching PostgREST `.range()`.
 */
export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize: number,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const page = await fetchPage(from, from + pageSize - 1);
    all.push(...page);
    if (page.length < pageSize) return all;
  }
}

/** Profiles -> people. Rows with no login are dropped: they cannot be assigned anything. */
export function toPeople(rows: readonly ProfileRow[]): Person[] {
  const people: Person[] = [];
  for (const row of rows) {
    if (!row.user_id) continue;
    const name = row.full_name?.trim() || row.email?.trim() || "Unknown";
    people.push({
      userId: row.user_id,
      name,
      email: row.email ?? null,
      isTestAccount: row.is_test_account === true,
      avatarUrl: row.avatar_url ?? null,
    });
  }
  return people.sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }));
}

export interface PeopleFilter {
  /** Never offer these (e.g. yourself, or the current assignee). */
  exclude?: readonly (string | null | undefined)[];
  /** When given, offer ONLY these login ids (e.g. people allowed to hold a batch-release task). */
  only?: readonly string[] | null;
}

export function filterPeople(people: readonly Person[], filter: PeopleFilter = {}): Person[] {
  const exclude = new Set((filter.exclude ?? []).filter((id): id is string => Boolean(id)));
  const only = filter.only ? new Set(filter.only) : null;
  return people.filter((p) => !exclude.has(p.userId) && (only === null || only.has(p.userId)));
}

/**
 * Case-insensitive search over name and email, for the searchable popovers (formulation
 * assignee, @-mentions). A blank term returns everyone; `limit` caps the result.
 */
export function searchPeople(people: readonly Person[], term: string, limit?: number): Person[] {
  const needle = term.trim().toLowerCase();
  const hits = needle
    ? people.filter((p) => `${p.name} ${p.email ?? ""}`.toLowerCase().includes(needle))
    : [...people];
  return limit === undefined ? hits : hits.slice(0, limit);
}

export function indexPeople(people: readonly Person[]): Map<string, Person> {
  return new Map(people.map((p) => [p.userId, p]));
}

/** The current name for a login id, else the stored fallback, else null. */
export function personName(
  byId: ReadonlyMap<string, Person>,
  userId: string | null | undefined,
  fallback: string | null | undefined,
): string | null {
  if (userId) {
    const person = byId.get(userId);
    if (person) return person.name;
  }
  return fallback ?? null;
}

/** The task_assignments id -> copied-name pairs that screens show. */
export const TASK_NAME_PAIRS = [
  ["assigned_to", "assigned_to_name"],
  ["assigned_by", "assigned_by_name"],
  ["reassigned_to", "reassigned_to_name"],
  ["escalated_to", "escalated_to_name"],
] as const;

type TaskNameRow = {
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  assigned_by?: string | null;
  assigned_by_name?: string | null;
  reassigned_to?: string | null;
  reassigned_to_name?: string | null;
  escalated_to?: string | null;
  escalated_to_name?: string | null;
};

/**
 * Replace a task row's copied names with the people's current names. A pair is only
 * touched when the row carries that id column; an id that doesn't resolve keeps its
 * stored name. Returns a new object — the input is not mutated.
 */
export function withLiveTaskNames<T extends TaskNameRow>(row: T, byId: ReadonlyMap<string, Person>): T {
  const out: T = { ...row };
  for (const [idKey, nameKey] of TASK_NAME_PAIRS) {
    if (!(idKey in row)) continue;
    const resolved = personName(byId, row[idKey], row[nameKey]);
    (out as TaskNameRow)[nameKey] = resolved;
  }
  return out;
}

/**
 * The copied name to WRITE alongside a login id when a form saves. The person's current
 * name when they can be found; otherwise, if the id is unchanged from what is stored, the
 * stored copy is kept — an edit made while the people list is unavailable, or after the
 * login was removed, must not erase the fallback name the live-name lookup relies on.
 * A changed id that can't be resolved, or no id, writes null.
 */
export function copiedNameToWrite(
  byId: ReadonlyMap<string, Person>,
  userId: string | null | undefined,
  stored: { id: string | null | undefined; name: string | null | undefined } | null | undefined,
): string | null {
  if (!userId) return null;
  const person = byId.get(userId);
  if (person) return person.name;
  if (stored && stored.id === userId) return stored.name ?? null;
  return null;
}
